/**
 * Subscription and Tier Enums
 *
 * Enums for rate limiting, portal types, and access levels.
 * Note: SubscriptionTier is defined in roles.ts
 * Issue #313 - Centralize enums to shared-types
 */

import { z } from 'zod';

// Note: SubscriptionTier is exported from roles.ts to avoid duplication

// ============================================================================
// RATE LIMIT TIER
// ============================================================================

/**
 * Rate limit tiers for API endpoints
 */
export enum RateLimitTier {
  /** Fast tier - higher rate limits */
  FAST = 'fast',
  /** Normal tier - standard rate limits */
  NORMAL = 'normal',
  /** Slow tier - lower rate limits for expensive operations */
  SLOW = 'slow',
}

/** Zod schema for RateLimitTier validation */
export const RateLimitTierSchema = z.nativeEnum(RateLimitTier);

/** Array of all rate limit tiers for iteration */
export const RATE_LIMIT_TIERS = Object.values(RateLimitTier);

// ============================================================================
// PORTAL TYPE
// ============================================================================

/**
 * Portal types
 */
export enum PortalType {
  ADMIN = 'admin',
  BUSINESS = 'business',
  CONSUMER = 'consumer',
}

/** Zod schema for PortalType validation */
export const PortalTypeSchema = z.nativeEnum(PortalType);

/** Array of all portal types for iteration */
export const PORTAL_TYPES = Object.values(PortalType);

// ============================================================================
// QUOTA STATUS
// ============================================================================

/**
 * Overall quota status
 */
export enum QuotaStatus {
  OK = 'ok',
  WARNING = 'warning',
  EXCEEDED = 'exceeded',
}

/** Zod schema for QuotaStatus validation */
export const QuotaStatusSchema = z.nativeEnum(QuotaStatus);

// ============================================================================
// RESOURCE TYPE (for quotas)
// ============================================================================

/**
 * Resource types for usage tracking and quotas
 */
export enum ResourceType {
  TOKENS = 'tokens',
  DOCUMENTS = 'documents',
  API_CALLS = 'api_calls',
  AGENTS = 'agents',
  CONVERSATIONS = 'conversations',
  AGENT_INVOCATIONS = 'agent_invocations',
}

/** Zod schema for ResourceType validation */
export const ResourceTypeSchema = z.nativeEnum(ResourceType);

/** Array of all resource types for iteration */
export const RESOURCE_TYPES = Object.values(ResourceType);

// ============================================================================
// BUSINESS SUBSCRIPTION STATUS
// ============================================================================

/**
 * Business subscription lifecycle status
 */
export enum BusinessSubscriptionStatus {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  PAST_DUE = 'past_due',
  TRIALING = 'trialing',
  PAUSED = 'paused',
}

/** Zod schema for BusinessSubscriptionStatus validation */
export const BusinessSubscriptionStatusSchema = z.nativeEnum(BusinessSubscriptionStatus);

/** Array of all business subscription statuses for iteration */
export const BUSINESS_SUBSCRIPTION_STATUSES = Object.values(BusinessSubscriptionStatus);

// ============================================================================
// BILLING CYCLE
// ============================================================================

/**
 * Billing cycle options
 */
export enum BillingCycle {
  MONTHLY = 'monthly',
  ANNUAL = 'annual',
}

/** Zod schema for BillingCycle validation */
export const BillingCycleSchema = z.nativeEnum(BillingCycle);

/** Array of all billing cycles for iteration */
export const BILLING_CYCLES = Object.values(BillingCycle);
